/**
 * Auth Profile Staleness Indicator (Phase 9)
 * Shows visual indicator of profile freshness/staleness
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faExclamationTriangle, faCircle, faSkullCrossbones } from '@fortawesome/free-solid-svg-icons';

const AuthProfileStalenessIndicator = ({ profile, isDarkMode, size = 'normal' }) => {
  // Check if profile is marked as stale
  if (profile.stalenessTracking?.isStale) {
    return (
      <div className={`flex items-center gap-1.5 ${
        size === 'small' ? 'text-xs' : 'text-sm'
      } text-red-600 dark:text-red-400 font-medium`}>
        <FontAwesomeIcon icon={faSkullCrossbones} className="text-red-500" />
        <span>STALE - Session expired</span>
      </div>
    );
  }

  // Calculate time since last update
  const lastRefreshed = profile.sessionRefresh?.lastRefreshedAt || profile.updatedAt || profile.createdAt;
  if (!lastRefreshed) {
    return (
      <div className={`flex items-center gap-1.5 ${
        size === 'small' ? 'text-xs' : 'text-sm'
      } text-gray-500`}>
        <FontAwesomeIcon icon={faCircle} className="text-xs" />
        <span>Never used</span>
      </div>
    );
  }

  const now = new Date();
  const lastRefreshedDate = new Date(lastRefreshed);
  const ageMs = now - lastRefreshedDate;
  const ageMinutes = Math.floor(ageMs / (1000 * 60));
  const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
  const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24));

  // Format age. For anything an hour or older, append the wall-clock time (and the
  // date once it's a day+ old) so "1 day ago" becomes "1 day ago (09:47)".
  const clock = lastRefreshedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dayStamp = lastRefreshedDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
  let ageText = '';
  if (ageMinutes < 1) {
    ageText = 'just now';
  } else if (ageMinutes < 60) {
    ageText = `${ageMinutes} ${ageMinutes === 1 ? 'minute' : 'minutes'} ago`;
  } else if (ageHours < 24) {
    ageText = `${ageHours} ${ageHours === 1 ? 'hour' : 'hours'} ago (${clock})`;
  } else {
    ageText = `${ageDays} ${ageDays === 1 ? 'day' : 'days'} ago (${dayStamp}, ${clock})`;
  }

  // Determine color and icon based on age
  let colorClass = '';
  let icon = faBolt;
  let label = 'Last updated';

  if (ageHours < 1) {
    // Fresh - Green with lightning bolt
    colorClass = 'text-green-600 dark:text-green-400';
    icon = faBolt;
  } else if (ageHours < 24) {
    // Recent - Green/blue
    colorClass = 'text-blue-600 dark:text-blue-400';
    icon = faCircle;
  } else if (ageDays < 7) {
    // Warning - Yellow
    colorClass = 'text-yellow-600 dark:text-yellow-400';
    icon = faExclamationTriangle;
  } else {
    // Critical - Red (but not marked as stale yet)
    colorClass = 'text-red-600 dark:text-red-400';
    icon = faExclamationTriangle;
  }

  return (
    <div className={`flex items-center gap-1.5 ${
      size === 'small' ? 'text-xs' : 'text-sm'
    } ${colorClass}`}>
      <FontAwesomeIcon
        icon={icon}
        className={`${size === 'small' ? 'text-[10px]' : 'text-xs'} ${
          icon === faBolt && ageMinutes < 5 ? 'animate-pulse' : ''
        }`}
      />
      <span className="font-medium">{label}:</span>
      <span>{ageText}</span>
    </div>
  );
};

// Compact badge version for profile cards
export const AuthProfileStalenessBadge = ({ profile, isDarkMode }) => {
  if (profile.stalenessTracking?.isStale) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
        STALE
      </span>
    );
  }

  const lastRefreshed = profile.sessionRefresh?.lastRefreshedAt || profile.updatedAt || profile.createdAt;
  if (!lastRefreshed) return null;

  const ageHours = Math.floor((new Date() - new Date(lastRefreshed)) / (1000 * 60 * 60));

  if (ageHours < 1) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
        ⚡ Fresh
      </span>
    );
  } else if (ageHours >= 24 && ageHours < 168) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
        ⚠️ {Math.floor(ageHours / 24)}d old
      </span>
    );
  } else if (ageHours >= 168) {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
        🔴 {Math.floor(ageHours / 24)}d old
      </span>
    );
  }

  return null;
};

export default AuthProfileStalenessIndicator;
